import { ApiData, ApiDescription, RequestsMethod, User } from '@shared/clientTypes';

export const startMentorPersonalDataExport: ApiDescription = {
  endpoint: '/user-management/mentors/:id/personal-data-export',
  method: RequestsMethod.Post,
};

export type StartMentorPersonalDataExport = ApiData<unknown, { message: string }>;

export const startStudentPersonalDataExport: ApiDescription = {
  endpoint: '/user-management/students/:id/personal-data-export',
  method: RequestsMethod.Post,
};

export type StartStudentPersonalDataExport = ApiData<unknown, { message: string }>;

export const startParentPersonalDataExport: ApiDescription = {
  endpoint: '/user-management/students/:id/parent-personal-data-export',
  method: RequestsMethod.Post,
};

export type StartParentPersonalDataExport = ApiData<unknown, { message: string }>;

export const sendUserEmail: ApiDescription = {
  endpoint: '/user-management/send-user-email',
  method: RequestsMethod.Post,
};

export type SendUserEmail = ApiData<
  {
    to: string;
    subject: string;
    text: string;
  },
  { message: string }
>;

export const setActiveStatus: ApiDescription = {
  endpoint: '/user-management/set-active-status',
  method: RequestsMethod.Post,
};
export type SetActiveStatus = ApiData<
  { userId: string; deactivate: boolean; reason?: string },
  unknown
>;

export const syncUserWithAuth0: ApiDescription = {
  endpoint: '/user-management/users/:id/sync-auth0',
  method: RequestsMethod.Post,
};

export type SyncUserWithAuth0 = ApiData<
  unknown,
  { status: 'already_linked' | 'linked' | 'imported' | 'pending'; message: string }
>;

export const changeUserTestState: ApiDescription = {
  endpoint: '/user-management/change-user-test-state',
  method: RequestsMethod.Post,
};

export type ChangeUserTestState = ApiData<{ userId: string; isTestUser: boolean }, unknown>;

export const migrateUsersToAuth0: ApiDescription = {
  endpoint: '/user-management/migrate-users-to-auth0',
  method: RequestsMethod.Post,
};

export type MigrateUsersToAuth0 = ApiData<unknown, unknown>;

export const updateUsersWithAuthID: ApiDescription = {
  endpoint: '/user-management/update-users-with-authid',
  method: RequestsMethod.Post,
};

export type UpdateUsersWithAuthID = ApiData<unknown, unknown>;

export const updateAllUsers: ApiDescription = {
  endpoint: '/user-management/update-users-from-auth0',
  method: RequestsMethod.Post,
};

export type UpdateUsersFromAuth0 = ApiData<unknown, unknown>;

export const cleanUsersWithAuthID: ApiDescription = {
  endpoint: '/user-management/clean-users-with-authid',
  method: RequestsMethod.Post,
};

export type CleanUsersWithAuthID = ApiData<unknown, unknown>;

export const deactivateUsersFromAgoraInAuth0: ApiDescription = {
  endpoint: '/user-management/deactivate-users-from-agora-in-auth0',
  method: RequestsMethod.Post,
};

export type DeactivateUsersFromAgoraInAuth0 = ApiData<unknown, unknown>;

export const migrateMentorStatuses: ApiDescription = {
  endpoint: '/user-management/migrate-mentor-statuses',
  method: RequestsMethod.Patch,
};

export type MigrateMentorStatuses = ApiData<unknown, unknown>;

export const sendActivationLink = {
  endpoint: '/user-management/send-activation-link',
  method: RequestsMethod.Post,
};

export type SendActivationLink = ApiData<{ id: string }, unknown>;

export const syncHubspotIBANs = {
  endpoint: '/user-management/sync-hubspot-ibans',
  method: RequestsMethod.Patch,
};

export type SyncHubspotIBANs = ApiData<unknown, unknown>;

export const populateUsersWithHarvesterToken = {
  endpoint: '/user-management/populate-users-with-harvester-token',
  method: RequestsMethod.Post,
};

export type PopulateUsersWithHarvesterToken = ApiData<unknown, unknown>;

export const checkDriveStatus: ApiDescription = {
  endpoint: '/user-management/check-drive-status',
  method: RequestsMethod.Get,
};

export type CheckDriveStatus = ApiData<
  {
    id: string;
    searchBy: string;
  },
  unknown
>;

export const deleteFolderContents = {
  endpoint: '/user-management/delete-folder-contents',
  method: RequestsMethod.Delete,
};

export type DeleteFolderContents = ApiData<
  {
    folderId: string;
  },
  unknown
>;

export const saveDriveIds: ApiDescription = {
  endpoint: '/user-management/save-drive-ids',
  method: RequestsMethod.Patch,
};

export type SaveDriveIds = ApiData<unknown, unknown>;

export const copyDocuments = {
  endpoint: '/user-management/copy-documents',
  method: RequestsMethod.Patch,
};

export type CopyDocuments = ApiData<
  {
    oldStudentBaseFolder: string;
    oldMentorBaseFolder: string;
  },
  unknown
>;

export const createShortcutsForAllMentors = {
  endpoint: '/user-management/create-shortcuts-for-all-mentors',
  method: RequestsMethod.Post,
};

export type CreateShortcutsForAllMentors = ApiData<unknown, unknown>;

export const createShortcutsForMentor = {
  endpoint: '/user-management/create-shortcuts-for-mentor',
  method: RequestsMethod.Post,
};

export type CreateShortcutsForMentor = ApiData<{ mentorId: string }, unknown>;

export const changeFoldersPermission = {
  endpoint: '/user-management/change-folders-permission',
  method: RequestsMethod.Patch,
};

export type ChangeFoldersPermission = ApiData<
  {
    oldStudentBaseFolder: string;
    oldMentorBaseFolder: string;
  },
  unknown
>;

export const deleteShortcuts = {
  endpoint: '/user-management/delete-shortcuts',
  method: RequestsMethod.Delete,
};

export type DeleteShortcuts = ApiData<unknown, unknown>;

export const signup = {
  endpoint: '/auth/signup',
  method: RequestsMethod.Post,
};

export type Signup = ApiData<
  {
    email: string;
    password: string;
    role: number;
  },
  unknown
>;

export const getUserData = {
  endpoint: '/auth/get-user-data',
  method: RequestsMethod.Get,
};

export type GetUserData = ApiData<unknown, User>;

export const validateRegistrationToken = {
  endpoint: '/auth/validateRegistrationToken',
  method: RequestsMethod.Get,
};

export type ValidateRegistrationToken = ApiData<
  {
    id: string;
    token: string;
  },
  unknown
>;

export const changeFoldersPermissionToContentManager = {
  endpoint: '/user-management/change-folders-permission-to-content-manager',
  method: RequestsMethod.Patch,
};

export type ChangeFoldersPermissionToContentManager = ApiData<unknown, unknown>;

export const compareAgoraUsersWithAuth0 = {
  endpoint: '/user-management/compare-agora-users-with-auth0',
  method: RequestsMethod.Get,
};

export type CompareAgoraUsersWithAuth0 = ApiData<unknown, unknown>;

export const splitFullNameIntoFirstNameAndLastName = {
  endpoint: '/user-management/split-full-name-into-first-name-and-last-name',
  method: RequestsMethod.Post,
};

export type SplitFullNameIntoFirstNameAndLastName = ApiData<unknown, unknown>;

export const processPhoneNumberGPT = {
  endpoint: '/user-management/process-phone-number-GPT',
  method: RequestsMethod.Post,
};

export type ProcessPhoneNumberGPT = ApiData<unknown, unknown>;

export const getProcessedPhoneNumberJSON = {
  endpoint: '/user-management/get-process-phone-number-GPT',
  method: RequestsMethod.Get,
};

export type GetProcessedPhoneNumberJSON = ApiData<unknown, unknown>;
